import type { ReactNode } from "react";
import type {
  NotificationState,
  ToastOptions,
} from "@UI/types/notifications.ts";
import {
  addToast,
  dismissToast,
  updateToast,
} from "@UI/components/Molecules/Notification/toastStore.ts";

/**
 * Imperative toast API. Signature-compatible with react-toastify so call
 * sites migrate by swapping the import path only:
 *
 *   toast.success("Saved");
 *   toast.error(<UnexpectedErrorMessage />, { id: "error" });
 *   const id = toast(<ProgressToast />, { duration: false }); // neutral / custom JSX
 *   toast.update(id, { content: <ProgressToast progress={50} /> });
 *   toast.dismiss(id);
 */

type ToastFn = (content: ReactNode, options?: ToastOptions) => string;

const withState =
  (state: NotificationState): ToastFn =>
  (content, options) =>
    addToast(content, { ...options, state });

const base: ToastFn = (content, options) => addToast(content, options);

interface ToastApi extends ToastFn {
  success: ToastFn;
  error: ToastFn;
  info: ToastFn;
  warning: ToastFn;
  update: typeof updateToast;
  dismiss: typeof dismissToast;
}

export const toast: ToastApi = Object.assign(base, {
  success: withState("success"),
  error: withState("error"),
  info: withState("info"),
  warning: withState("warning"),
  update: updateToast,
  dismiss: dismissToast,
});
